import { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { useDispatch } from "react-redux";

import { User } from "pwapi";

import {
  ScaleInfo,
  TextAnnotation,
  ViewerMode,
  XYPoint,
} from "custom-models/ViewerTypes";

import { changeViewerMode } from "shared/actions/slideDetailActions";
import { createTextAnnotation } from "shared/utils/annotations";

import ViewerFacade from "../ViewerFacade";

interface TextToolProps {
  scaleInfo: ScaleInfo;
  viewerFacade: ViewerFacade;
  user: User;
  onAnnotationComplete: (ann: TextAnnotation) => void;
}

const TextTool = (props: TextToolProps) => {
  const { scaleInfo, viewerFacade, user, onAnnotationComplete } = props;
  const dispatch = useDispatch();

  const [point, setPoint] = useState<XYPoint | null>(null);
  const [enableOk, setEnableOk] = useState<boolean>(false);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const handlerId = props.viewerFacade.addHandler("canvas-click", (event) => {
      event.preventDefaultAction = true;
      const coords = props.viewerFacade.imageCoords(event);
      if (
        coords &&
        coords.x >= 0 &&
        coords.y >= 0 &&
        coords.x < scaleInfo.width &&
        coords.y < scaleInfo.height
      ) {
        setPoint({ x: coords.x, y: coords.y });
      }
    });
    return () => {
      viewerFacade?.removeHandler(handlerId);
    };
  }, []);

  const narrowLength = 2 / scaleInfo.scaleX;
  const wideLength = 18 / scaleInfo.scaleX;

  return (
    <>
      {point ? (
        <>
          <rect
            key="TextTool-crossbar-vertical"
            x={point.x - narrowLength / 2}
            y={point.y - wideLength / 2}
            width={narrowLength}
            height={wideLength}
            transform={`rotate(${-scaleInfo.rotation} ${point.x} ${point.y})`}
          />
          <rect
            key="TextTool-crossbar-horizontal"
            x={point.x - wideLength / 2}
            y={point.y - narrowLength / 2}
            width={wideLength}
            height={narrowLength}
            transform={`rotate(${-scaleInfo.rotation} ${point.x} ${point.y})`}
          />
        </>
      ) : (
        <></>
      )}
      <Modal
        isOpen={!!point}
        onRequestClose={() => {
          setPoint(null);
        }}
        contentLabel="POINT LABEL"
        className="Modal"
        overlayClassName="Overlay"
      >
        <div className="TextToolModal">
          <h3>ENTER TEXT:</h3>
          <textarea
            autoFocus={true}
            rows={5}
            cols={40}
            placeholder=""
            ref={textareaRef}
            onChange={() => {
              const val = (textareaRef.current?.value || "").trim();
              setEnableOk(val.length !== 0);
            }}
          />
          <nav>
            <button
              disabled={!enableOk}
              onClick={() => {
                const labelText = (textareaRef.current?.value || "").trim();
                if (labelText.length > 0) {
                  onAnnotationComplete(
                    createTextAnnotation(
                      undefined,
                      "",
                      user,
                      null,
                      point,
                      labelText
                    )
                  );
                }
                setPoint(null);
                dispatch(changeViewerMode(ViewerMode.PAN_ZOOM));
              }}
            >
              OK
            </button>
            <button
              onClick={() => {
                setPoint(null);
              }}
            >
              CANCEL
            </button>
          </nav>
        </div>
      </Modal>
    </>
  );
};

export default TextTool;
